import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Dialog as MuiDialog,
  Stack,
} from '@mui/material'
import { isNil } from 'lodash'
import { RxCross2 } from 'react-icons/rx'
import { Else, If, Then } from 'react-if'
import { tss } from 'tss-react'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { useTypedTranslation } from '../../../../common/hooks/useTypedTranslation'
import { Button } from '../../atoms/button/Button'
import { IconButton } from '../../atoms/icon-button/IconButton'
import { Text } from '../../atoms/text/Text'
import { ModalProps } from './types'
import {
  getModalBasePrimitives,
  modalActionsPrimitives,
  modalContentPrimitives,
  modalTitlePrimitives,
  modalTitleTextPrimitives,
} from './variants/primitives'

export function Modal({
  open,
  onClose,
  title,
  children,
  className,
  actions,
  fullWidth = false,
  showContentBorder = false,
  showCancelButton = true,
  showCloseButton = true,
  padContent = true,
  backgroundColor = '',
  onBackdropClose,
  ...rest
}: ModalProps) {
  const { t } = useTypedTranslation()
  const { isMobile } = useDeviceType()
  const { classes, cx } = useStyles({
    fullWidth: fullWidth || rest.fullScreen === true,
    fullHeight: rest.fullScreen === true,
    showContentBorder,
    padContent,
    backgroundColor,
    isFullScreenMobileModal: rest.fullScreen === true && isMobile,
  })

  const hasTitle = !isNil(title)

  return (
    <MuiDialog
      className={cx(classes.root, className)}
      fullWidth={fullWidth}
      onClick={(event) => event.stopPropagation()}
      onClose={(event, reason) => {
        if (reason === 'backdropClick' && onBackdropClose) {
          onBackdropClose(event as React.MouseEvent<HTMLButtonElement>)
        } else {
          onClose?.(event as React.MouseEvent<HTMLButtonElement>)
        }
      }}
      open={open}
      {...rest}
    >
      {hasTitle && (
        <DialogTitle className={classes.title}>
          <If condition={typeof title === 'string'}>
            <Then>
              <Text className={classes.titleText} typography="heading2">
                {title}
              </Text>
            </Then>
            <Else>{title}</Else>
          </If>
          {onClose && showCloseButton && (
            <IconButton onClick={onClose} size={'small'} variant={'ghost'}>
              <RxCross2 />
            </IconButton>
          )}
        </DialogTitle>
      )}
      {children && (
        <DialogContent className={cx(classes.content, className)}>{children}</DialogContent>
      )}
      {actions && (
        <DialogActions className={classes.actions}>
          {showCancelButton && (
            <Button onClick={onClose} variant={'ghost'}>
              {t('common.actions.cancel')}
            </Button>
          )}
          <Stack direction="row" gap={1}>
            {actions}
          </Stack>
        </DialogActions>
      )}
    </MuiDialog>
  )
}

const useStyles = tss
  .withName('DossModal')
  .withParams<{
    fullWidth: boolean
    fullHeight: boolean
    showContentBorder: boolean
    padContent: boolean
    backgroundColor: string
    isFullScreenMobileModal: boolean
  }>()
  .create(
    ({
      fullWidth,
      fullHeight,
      showContentBorder,
      padContent,
      backgroundColor,
      isFullScreenMobileModal,
    }) => {
      return {
        actions: modalActionsPrimitives,
        title: modalTitlePrimitives,
        root: getModalBasePrimitives(
          fullWidth,
          fullHeight,
          backgroundColor,
          isFullScreenMobileModal,
        ),
        content: modalContentPrimitives(showContentBorder, padContent),
        titleText: modalTitleTextPrimitives,
      }
    },
  )
